import HeaderNavigation from "components/PeopleCraft/HeaderNavigation";
import React, { useEffect, useState } from "react";

import { FaSpinner } from "react-icons/fa";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import Done from "@material-ui/icons/Done";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import { GoAlert } from "react-icons/go";

import "./styles.scss";
import { CustomDialog } from "components/Dialogs";
import Loading from "components/Loading";
import IntegrationAPI from "services/IntegrationAPI";

const Feedz = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [idIntegration, setIdIntegration] = useState<number | null>(null);
  const [learningHubToken, setLearningHubToken] = useState<string>("");
  const [isGeneratingToken, setIsGeneratingToken] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [feedzToken, setFeedzToken] = useState<string>("");
  const [isValidatingToken, setIsValidatingToken] = useState<boolean>(false);
  const [validationStatus, setValidationStatus] = useState<"PENDING" | "INVALID" | "VALID">(
    "PENDING"
  );
  const [validationMessage, setValidationMessage] = useState("");
  const [confirmationModalToShow, setConfirmationModalToShow] = useState<
    "NONE" | "ACTIVATE" | "DEACTIVATE"
  >("NONE");
  const [integrationActive, setIntegrationActive] = useState<boolean>(false);

  const handleGenerateToken = async () => {
    setIsGeneratingToken(true);
    setLearningHubToken("");
    setCopied(false);
    setValidationStatus("PENDING");

    IntegrationAPI.createFeedzIntegration()
      .then(loadData)
      .catch((error) => {
        console.error("Error creating Feedz integration:", error);
      })
      .finally(() => {
        setIsGeneratingToken(false);
      });
  };

  const handleCopyToken = async () => {
    try {
      await navigator.clipboard.writeText(learningHubToken);
      setCopied(true);
    } catch {}
  };

  const handleChangeFeedzToken = (e) => {
    setFeedzToken(e.target.value);
    setValidationStatus("PENDING");
    setValidationMessage("");
  };

  const handleValidateToken = async () => {
    if (!feedzToken) return;

    setIsValidatingToken(true);
    setValidationStatus("PENDING");

    try {
      await IntegrationAPI.validateFeedzToken(feedzToken);
      setValidationStatus("VALID");
      setValidationMessage("Token validado com sucesso!");
    } catch (error) {
      setValidationStatus("INVALID");

      const message = error?.response?.data?.message;
      switch (message) {
        case "Integration already validated":
          setValidationStatus("VALID");
          setValidationMessage("Token validado com sucesso!");
          break;
        case "Token already in use for another integration":
          setValidationMessage("Token em uso por outra integração.");
          break;
        default:
          setValidationMessage("Token inválido, confira o código e tente novamente.");
          break;
      }
    } finally {
      setIsValidatingToken(false);
    }
  };

  const handleActivateStatus = (active: boolean) => {
    const methodName = active ? "activateIntegration" : "deactivateIntegration";

    IntegrationAPI[methodName](idIntegration!)
      .then(() => {
        setIntegrationActive(active);
        setConfirmationModalToShow("NONE");
      })
      .catch((error) => {
        console.error("Error updating integration status:", error);
        setConfirmationModalToShow("NONE");
      });
  };

  const loadData = () => {
    IntegrationAPI.getFeedzIntegration()
      .then((response) => {
        const { result } = response?.data;

        if (result) {
          setIdIntegration(result.idIntegration || null);
          setLearningHubToken(result.token || "");
          setFeedzToken(result.remoteToken || "");
          setIntegrationActive(result.active || false);
          setValidationStatus(result.remoteToken ? "VALID" : "PENDING");
        }
      })
      .catch((error) => {
        console.error("Error fetching Feedz integration data:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(loadData, []);

  if (loading) {
    return <Loading> Carregando... </Loading>;
  }

  const canActivateIntegration = validationStatus === "VALID";

  return (
    <div className="integrations-feedz">
      <HeaderNavigation title="Feedz" />

      <h4 className="header-title title">Integração FEEDZ</h4>
      <span className="subtitle">Para ativar a integração com a plataforma Feedz:</span>

      <ol className="integration-steps">
        <li>
          Clique em <strong>gerar token</strong>, para gerar o token a ser informado na plataforma
          Feedz.
        </li>
        <li>
          Vá até a área de configurações e <strong>informe o token Learning Hub</strong>.
        </li>
        <li>
          Informe o token gerado e clique em <strong>validar token</strong>.
        </li>
        <li>
          Clique no botão <strong>ativar integração</strong>.
        </li>
      </ol>

      <div className="token-section">
        <h5 className="token-title">Token</h5>

        <div className="form-group">
          <label>Learning Hub token</label>

          <div className="input-with-button">
            <div className="input-wrapper">
              <input
                type="text"
                className="form-control"
                placeholder={
                  isGeneratingToken
                    ? "Gerando token..."
                    : 'Clique em "Gerar token" para gerar o token a ser informado na Feedz...'
                }
                value={learningHubToken}
                readOnly
                disabled
              />

              {learningHubToken && (
                <button
                  type="button"
                  className="input-icon input-icon-copy"
                  onClick={handleCopyToken}
                >
                  <FileCopyIcon fontSize="small" />
                </button>
              )}
            </div>

            <button
              type="button"
              className="btn btn-primary"
              disabled={isGeneratingToken || !!learningHubToken}
              onClick={handleGenerateToken}
            >
              {isGeneratingToken ? (
                <FaSpinner className="spin" color="#FFF" size={16} />
              ) : (
                "Gerar token"
              )}
            </button>
          </div>

          {copied && <div className="helper-text helper-text-success">Token copiado!</div>}
        </div>

        <div className="form-group">
          <label>Feedz token</label>

          <div className="input-with-button">
            <div className="input-wrapper">
              <input
                type="text"
                value={feedzToken}
                onChange={handleChangeFeedzToken}
                placeholder="Digite ou cole o token gerado na Feedz..."
                className={`form-control ${
                  validationStatus === "INVALID"
                    ? "is-invalid"
                    : validationStatus === "VALID"
                    ? "is-valid"
                    : ""
                }`}
              />

              {validationStatus === "INVALID" && (
                <span className="input-icon input-icon-error">
                  <ErrorOutline fontSize="small" />
                </span>
              )}

              {validationStatus === "VALID" && (
                <span className="input-icon input-icon-success">
                  <Done fontSize="small" />
                </span>
              )}
            </div>

            <button
              type="button"
              className="btn btn-primary"
              disabled={isValidatingToken || !feedzToken || validationStatus === "VALID"}
              onClick={handleValidateToken}
            >
              {isValidatingToken ? (
                <FaSpinner className="spin" color="#FFF" size={16} />
              ) : (
                "Validar"
              )}
            </button>
          </div>

          {validationMessage && (
            <div
              className={`helper-text ${
                validationStatus === "INVALID" ? "helper-text-error" : "helper-text-success"
              }`}
            >
              {validationMessage}
            </div>
          )}
        </div>

        {integrationActive ? (
          <button
            type="button"
            className="btn btn-danger"
            disabled={!canActivateIntegration}
            onClick={() => setConfirmationModalToShow("DEACTIVATE")}
          >
            Desativar integração
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-success"
            disabled={!canActivateIntegration}
            onClick={() => setConfirmationModalToShow("ACTIVATE")}
          >
            Ativar integração
          </button>
        )}
      </div>

      <CustomDialog
        show={confirmationModalToShow === "ACTIVATE"}
        onClose={() => {
          setConfirmationModalToShow("NONE");
        }}
        title="Ativar integração?"
        description={`Tem certeza que deseja ativar a integração com a <strong>Feedz</strong>?`}
        icon={
          <div className="integration-warning-icon-wrapper">
            <GoAlert className="warning-icon" />
          </div>
        }
        buttons={[
          {
            label: "Ativar integração",
            onClick: () => {
              handleActivateStatus(true);
            },
            variant: "success",
            className: "btn",
          },
        ]}
      />

      <CustomDialog
        show={confirmationModalToShow === "DEACTIVATE"}
        onClose={() => {
          setConfirmationModalToShow("NONE");
        }}
        title="Desativar integração?"
        description={`Tem certeza que deseja desativar a integração com a <strong>Feedz</strong>?`}
        icon={
          <div className="integration-warning-icon-wrapper">
            <GoAlert className="warning-icon" />
          </div>
        }
        buttons={[
          {
            label: "Desativar integração",
            onClick: () => {
              handleActivateStatus(false);
            },
            variant: "danger",
            className: "btn",
          },
        ]}
      />
    </div>
  );
};

export default Feedz;
